import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMapping, listPrefillKeys } from "utils/collegeDocumentService";
import { MAPPING_QUERY_KEY, PREFILL_KEYS_QUERY_KEY } from "./documentLibraryQueryKeys";

/**
 * Loads one file's mapping (name, page previews, fields) and the prefill-key catalog, and keeps
 * a local editable copy of the field list. The local copy is deliberately re-synced from the
 * server on every fresh `mappingQuery.data` (initial load, post-save refetch, post-copy refetch)
 * rather than merged. This page has no offline/concurrent-edit story, so "server is truth
 * whenever we just heard from it" is simpler and avoids silently keeping stale local fields.
 */
export const useMappingData = (fileId) => {
  const [fields, setFields] = useState([]);

  const mappingQuery = useQuery({
    queryKey: [MAPPING_QUERY_KEY, fileId],
    queryFn: () => getMapping(fileId),
    enabled: Boolean(fileId),
  });

  const prefillQuery = useQuery({
    queryKey: [PREFILL_KEYS_QUERY_KEY],
    queryFn: listPrefillKeys,
  });

  useEffect(() => {
    if (mappingQuery.data) {
      setFields(mappingQuery.data.fields || []);
    }
  }, [mappingQuery.data]);

  const prefillGroups = useMemo(() => prefillQuery.data || [], [prefillQuery.data]);

  const prefillByKey = useMemo(() => {
    const map = {};
    prefillGroups.forEach((group) => {
      (group.keys || []).forEach((key) => {
        map[key.key] = key;
      });
    });
    return map;
  }, [prefillGroups]);

  return { fields, setFields, mappingQuery, prefillQuery, prefillGroups, prefillByKey };
};
